import { api } from '../api.js';
import { el, clear, openDialog } from '../dom.js';
import { t, formatDate } from '../i18n.js';

// Three people run one club. Individual membership is the answer to the
// shared login, and it only works if removing somebody is as easy as adding
// them — a credential you cannot revoke is not access control.
export async function openMembersForm({ venue, onSaved }) {
  const body = el('div', { class: 'members' });
  const error = el('p', { class: 'pub-error', role: 'alert' });
  error.hidden = true;

  const email = el('input', { id: 'mf-email', type: 'email', autocomplete: 'off' });
  const role = el('select', { id: 'mf-role' }, [
    el('option', { value: 'editor' }, t.roleEditor),
    el('option', { value: 'owner' }, t.roleOwner),
  ]);
  const invite = el('button', { class: 'button', type: 'submit' }, t.sendInvite);

  // Owner-only, like the route it posts to — an editor who could invite could
  // invite an account of their own as owner and be one. The remove button
  // below was already gated on this flag and the form beside it was not, which
  // is the `canManage` decision applied to half the dialog it was written for.
  // Unreachable today because the page that opens this refuses an editor
  // first — and unreachable-by-accident is what stops being true the moment
  // somebody opens the dialog from a second place.
  const form = el('form', { class: 'form form-wide' }, [
    el('h2', { class: 'dialog-title' }, t.team),
    el('p', { class: 'card-meta' }, t.teamHint),
    error,
    body,
    venue.canManage
      ? el('div', { class: 'row' }, [
          el('label', { for: 'mf-email' }, t.fieldEmail), email,
          el('label', { for: 'mf-role' }, t.fieldRole), role,
          invite,
        ])
      : el('p', { class: 'card-meta' }, t.ownerOnly),
    el('div', { class: 'row' }, [
      el('button', { class: 'button button-quiet', type: 'button' }, t.close),
    ]),
  ]);

  const dialog = openDialog(form);

  form.querySelector('.button-quiet').addEventListener('click', () => dialog.close());
  // openDialog() removes the node on close; this only adds the refresh.
  dialog.addEventListener('close', () => onSaved?.());

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    // Belt to the hidden form's braces: a <form> submits on Enter from any
    // field it still contains, so hiding the button alone is not the same as
    // not being able to send it.
    if (!venue.canManage) return;
    if (!email.value.trim()) return;
    invite.disabled = true;
    try {
      await api.post(`/accounts/${venue.id}/invites`, { email: email.value.trim(), role: role.value });
      email.value = '';
      error.hidden = true;
      await paint();
    } catch {
      error.textContent = t.inviteFailed;
      error.hidden = false;
    } finally {
      invite.disabled = false;
    }
  });

  await paint();

  async function paint() {
    const { members, invites } = await api.get(`/accounts/${venue.id}/members`);
    const open = invites.filter((row) => !row.acceptedAt);

    clear(body);
    body.append(el('ul', { class: 'card-list' }, [
      ...members.map(memberRow),
      ...open.map(inviteRow),
    ]));
  }

  function memberRow(member) {
    const remove = el('button', { class: 'button button-quiet', type: 'button' }, t.removeMember);
    remove.addEventListener('click', async () => {
      if (!window.confirm(t.confirmRemoveMember)) return;
      remove.disabled = true;
      try {
        await api.del(`/accounts/${venue.id}/members/${member.userId}`);
        await paint();
      } catch (error_) {
        error.textContent = error_.code === 'last_owner' ? t.lastOwner : t.actionFailed;
        error.hidden = false;
        remove.disabled = false;
      }
    });

    return el('li', {}, el('div', { class: 'card row' }, [
      el('span', {}, member.displayName ?? member.email),
      el('span', { class: 'badge' }, member.role === 'owner' ? t.roleOwner : t.roleEditor),
      venue.canManage ? el('span', { class: 'row-end' }, remove) : null,
    ].filter(Boolean)));
  }

  function inviteRow(row) {
    const revoke = el('button', { class: 'button button-quiet', type: 'button' }, t.revokeInvite);
    revoke.addEventListener('click', async () => {
      revoke.disabled = true;
      await api.del(`/accounts/${venue.id}/invites/${row.id}`);
      await paint();
    });

    return el('li', {}, el('div', { class: 'card row' }, [
      el('span', { class: 'card-meta' }, row.email),
      el('span', { class: 'badge badge-draft' }, t.invitePending),
      el('span', { class: 'card-meta' }, `${t.untilLabel} ${formatDate(row.expiresAt.slice(0, 10))}`),
      // Owner-only on the route, same as the member row below it. This one was
      // missed when that gate was added — the pending-invite row is easy to
      // read as display and it carries a write.
      venue.canManage ? el('span', { class: 'row-end' }, revoke) : null,
    ].filter(Boolean)));
  }
}
